import {baseApi} from './base'
import type {
  LogbookEntryInterface,
  EquipmentLogbookEntriesResult,
  LogbookCategoryInterface,
  SingleEquipmentLogbookEntryResult,
} from '@interfaces/logbook/logbook'
import type {PaginatedResponse} from '@interfaces/common/paginatedResponse'

const entriesAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getLogbookEntries: builder.query<
      EquipmentLogbookEntriesResult,
      {
        entryType: string
        targetId: string
        tStart: string
        tEnd: string
        summarizeBy?: string
      }
    >({
      query: ({entryType, targetId, tStart, tEnd, summarizeBy}) => {
        // Determine the target type based on entryType
        const targetType =
          entryType === 'line'
            ? 'machine'
            : entryType === 'equipment'
              ? 'machine_sub_system'
              : entryType
        let url = `logbook/timeline/?target_type=${targetType}&target=${targetId}&`
        if (tStart) {
          url += `t_start=${tStart}&`
        }
        if (tEnd) {
          url += `t_end=${tEnd}&`
        }
        if (summarizeBy) {
          url += `summarize_by=${summarizeBy}`
        }
        // Remove trailing '&' or '?' if present
        url = url.endsWith('&') || url.endsWith('?') ? url.slice(0, -1) : url
        return {url}
      },
    }),
    getLogbookEntriesBatch: builder.query<
      Record<string, EquipmentLogbookEntriesResult>,
      {
        entryType: string
        targetIds: string[]
        tStart: string
        tEnd: string
        summarizeBy?: string
      }
    >({
      async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
        const {entryType, targetIds, tStart, tEnd, summarizeBy} = args
        const targetType =
          entryType === 'line'
            ? 'machine'
            : entryType === 'equipment'
              ? 'machine_sub_system'
              : entryType

        try {
          const results: Record<string, EquipmentLogbookEntriesResult> = {}

          // Fetch entries for each targetId
          const promises = targetIds.map(async (targetId) => {
            let url = `logbook/timeline/?target_type=${targetType}&target=${targetId}&`
            if (tStart) {
              url += `t_start=${tStart}&`
            }
            if (tEnd) {
              url += `t_end=${tEnd}&`
            }
            if (summarizeBy) {
              url += `summarize_by=${summarizeBy}`
            }
            url = url.endsWith('&') || url.endsWith('?') ? url.slice(0, -1) : url

            const response = await fetchWithBQ({url})
            if (response.error) {
              throw response.error
            }

            results[targetId] = response.data as EquipmentLogbookEntriesResult

            return {targetId, data: response.data}
          })

          await Promise.all(promises)
          return {data: results}
        } catch (error) {
          return {error: {status: 'FETCH_ERROR', error: String(error)}}
        }
      },
    }),
    getLogbookEntry: builder.query<
      SingleEquipmentLogbookEntryResult,
      {
        entryType: string
        entryId: string
      }
    >({query: ({entryId}) => ({url: `logbook/${entryId}/`})}),
    createLogbookEntry: builder.mutation<LogbookEntryInterface, Record<string, string | null>>({
      query: (body) => ({
        url: 'logbook/',
        method: 'POST',
        body,
      }),
    }),
    updateLogbookEntry: builder.mutation<
      LogbookEntryInterface[],
      {
        entryType: string
        entryId: string
        body: Record<string, string | null>
      }
    >({
      query: ({entryId, body}) => ({
        url: `logbook/${entryId}/`,
        method: 'PATCH',
        body,
      }),
    }),
    // Authors only; anyone else fixes a mistake with a correction instead.
    deleteLogbookEntry: builder.mutation<void, {entryId: string}>({
      query: ({entryId}) => ({url: `logbook/${entryId}/`, method: 'DELETE'}),
    }),
    getLogbookCategories: builder.query<
      PaginatedResponse<LogbookCategoryInterface>,
      {limit?: number; offset?: number; search?: string; ordering?: string}
    >({
      query: ({limit, offset, search, ordering}) => ({
        url: 'logbook-categories/',
        params: {
          ...(limit !== undefined ? {limit} : {}),
          ...(offset !== undefined ? {offset} : {}),
          ...(search ? {search} : {}),
          ...(ordering ? {ordering} : {}),
        },
      }),
      providesTags: ['LogbookCategory'],
    }),
    createLogbookCategory: builder.mutation<
      {message: string; data: LogbookCategoryInterface},
      {name: string; description?: string}
    >({
      query: (body) => ({url: 'logbook-categories/', method: 'POST', body}),
      invalidatesTags: ['LogbookCategory'],
    }),
    updateLogbookCategory: builder.mutation<
      {message: string; data: LogbookCategoryInterface},
      {id: string; data: {name?: string; description?: string}}
    >({
      query: ({id, data}) => ({url: `logbook-categories/${id}/`, method: 'PATCH', body: data}),
      invalidatesTags: ['LogbookCategory'],
    }),
    deleteLogbookCategory: builder.mutation<void, {id: string}>({
      query: ({id}) => ({url: `logbook-categories/${id}/`, method: 'DELETE'}),
      invalidatesTags: ['LogbookCategory'],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetLogbookEntriesQuery,
  useGetLogbookEntriesBatchQuery,
  useGetLogbookEntryQuery,
  useCreateLogbookEntryMutation,
  useUpdateLogbookEntryMutation,
  useDeleteLogbookEntryMutation,
  useGetLogbookCategoriesQuery,
  useCreateLogbookCategoryMutation,
  useUpdateLogbookCategoryMutation,
  useDeleteLogbookCategoryMutation,
} = entriesAPI
